import {
  Col,
  Flex,
  Form,
  Input,
  JsonEditor,
  type JsonValue,
  Paragraph,
  Row,
  Select,
  Switch,
  type FormInstance,
  type SelectOption,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { useCallback, useEffect, useMemo } from "react";

export interface PortalCredentialAddEditFormValues {
  purchaserId: number | null;
  portalKey: string;
  portalUserName: string;
  portalPassword: string;
  isActive: boolean;
  metadata: string;
}

export interface PurchaserOption {
  purchaserId: number;
  purchaserName: string;
  purchaserKey: string;
}

export interface PortalKeyOption {
  portalKey: string;
  portalKeyName: string;
}

interface ConfigPortalCredentialAddEditFormProps {
  form: FormInstance<PortalCredentialAddEditFormValues>;
  mode: "create" | "edit";
  initialValues: PortalCredentialAddEditFormValues;
  purchaserOptions: PurchaserOption[];
  purchasersLoading?: boolean;
  portalKeyOptions: PortalKeyOption[];
  portalKeysLoading?: boolean;
  lockedPurchaserName?: string;
  lockedPortalKey?: string;
  onPurchaserChange?: (
    purchaserId: number | null,
    purchaserKey: string | null,
  ) => void;
  onSubmit: (values: PortalCredentialAddEditFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

function parseIdFromSelect(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === "object" && "value" in (value as object)) {
    const raw = (value as SelectOption).value;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function parseKeyFromSelect(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && "value" in (value as object)) {
    return String((value as SelectOption).value ?? "");
  }
  return String(value);
}

function parseStoredJson(value: string | undefined): JsonValue {
  const trimmed = (value ?? "").trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed != null && typeof parsed === "object") {
      return parsed as JsonValue;
    }
    return {};
  } catch {
    return {};
  }
}

function normalizeMetadataForCompare(value: string | undefined): string {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return "";
  try {
    return JSON.stringify(JSON.parse(trimmed));
  } catch {
    return trimmed;
  }
}

interface PurchaserSelectFieldProps {
  value?: number | null;
  onChange?: (value: number | null) => void;
  options: SelectOption[];
  isLoading?: boolean;
  selectKey: string;
  onPurchaserSelected?: () => void;
}

const PurchaserSelectField = ({
  value,
  onChange,
  options,
  isLoading = false,
  selectKey,
  onPurchaserSelected,
}: PurchaserSelectFieldProps) => {
  const selected = options.find((option) => option.value === String(value));
  return (
    <Select
      key={`${selectKey}-${value ?? "none"}-${options.length}`}
      size="medium"
      isSearchable
      isClearable={false}
      isLoading={isLoading}
      placeholder="Select purchaser"
      options={options}
      defaultValue={selected}
      onChange={(next) => {
        onChange?.(parseIdFromSelect(next));
        onPurchaserSelected?.();
      }}
      aria-label="Purchaser"
    />
  );
};

interface PortalKeySelectFieldProps {
  value?: string;
  onChange?: (value: string) => void;
  options: SelectOption[];
  isLoading?: boolean;
  isDisabled?: boolean;
  placeholder: string;
  selectKey: string;
}

const PortalKeySelectField = ({
  value,
  onChange,
  options,
  isLoading = false,
  isDisabled = false,
  placeholder,
  selectKey,
}: PortalKeySelectFieldProps) => {
  const selected = options.find((option) => option.value === value);
  return (
    <Select
      key={`${selectKey}-${value || "none"}-${options.length}`}
      size="medium"
      isSearchable
      isClearable={false}
      isDisabled={isDisabled}
      isLoading={isLoading}
      placeholder={placeholder}
      options={options}
      defaultValue={selected}
      onChange={(next) => onChange?.(parseKeyFromSelect(next))}
      aria-label="Portal Key"
    />
  );
};

interface JsonEditorFieldProps {
  value?: string;
  onChange?: (next: string) => void;
}

const JsonEditorField = ({ value, onChange }: JsonEditorFieldProps) => {
  return (
    <JsonEditor
      value={parseStoredJson(value)}
      onChange={(next) => onChange?.(JSON.stringify(next, null, 2))}
      rootName="metadata"
      collapse={2}
    />
  );
};

export const ConfigPortalCredentialAddEditForm = ({
  form,
  mode,
  initialValues,
  purchaserOptions,
  purchasersLoading = false,
  portalKeyOptions,
  portalKeysLoading = false,
  lockedPurchaserName,
  lockedPortalKey,
  onPurchaserChange,
  onSubmit,
  onFormValidityChange,
}: ConfigPortalCredentialAddEditFormProps) => {
  const purchaserId = Form.useWatch("purchaserId", form) as number | null;

  const purchaserSelectOptions = useMemo<SelectOption[]>(
    () =>
      purchaserOptions.map((purchaser) => ({
        value: String(purchaser.purchaserId),
        label: `${purchaser.purchaserName} (${purchaser.purchaserKey})`,
      })),
    [purchaserOptions],
  );

  const portalKeySelectOptions = useMemo<SelectOption[]>(
    () =>
      portalKeyOptions.map((key) => ({
        value: key.portalKey,
        label: key.portalKeyName
          ? `${key.portalKeyName} (${key.portalKey})`
          : key.portalKey,
      })),
    [portalKeyOptions],
  );

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const portalUserName = values.portalUserName?.trim() ?? "";
    const portalPassword = values.portalPassword?.trim() ?? "";
    const portalKey = values.portalKey?.trim() ?? "";
    const formPurchaserId = values.purchaserId;

    const hasRequiredValues =
      portalUserName !== "" &&
      (mode === "edit"
        ? true
        : formPurchaserId != null &&
          Number(formPurchaserId) > 0 &&
          portalKey !== "" &&
          portalPassword !== "");

    const isDirty =
      mode === "create"
        ? true
        : portalUserName !== (initialValues.portalUserName?.trim() ?? "") ||
          (values.portalPassword?.trim() ?? "") !==
            (initialValues.portalPassword?.trim() ?? "") ||
          Boolean(values.isActive) !== Boolean(initialValues.isActive) ||
          normalizeMetadataForCompare(values.metadata) !==
            normalizeMetadataForCompare(initialValues.metadata);

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  useEffect(() => {
    if (mode !== "create") return;
    if (purchaserId == null || Number(purchaserId) <= 0) {
      onPurchaserChange?.(null, null);
      return;
    }
    const match = purchaserOptions.find(
      (item) => item.purchaserId === Number(purchaserId),
    );
    onPurchaserChange?.(Number(purchaserId), match?.purchaserKey ?? null);
  }, [mode, onPurchaserChange, purchaserId, purchaserOptions]);

  const portalKeyEnabled =
    mode === "edit" || (purchaserId != null && Number(purchaserId) > 0);

  const handlePurchaserSelected = useCallback(() => {
    form.setFieldValue("portalKey", "");
    updateFormValidity();
  }, [form, updateFormValidity]);

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        className="config-portal-credential-add-edit"
      >
        <Row>
          <Col span={6}>
            {mode === "edit" ? (
              <Form.Item label="Purchaser">
                <Input value={lockedPurchaserName ?? ""} isDisabled />
              </Form.Item>
            ) : (
              <Form.Item
                name="purchaserId"
                label="Purchaser"
                rules={[{ required: true, message: "Purchaser is required" }]}
              >
                <PurchaserSelectField
                  options={purchaserSelectOptions}
                  isLoading={purchasersLoading}
                  selectKey="config-purchaser"
                  onPurchaserSelected={handlePurchaserSelected}
                />
              </Form.Item>
            )}
          </Col>
          <Col span={6}>
            {mode === "edit" ? (
              <Form.Item label="Portal Key">
                <Input value={lockedPortalKey ?? ""} isDisabled />
              </Form.Item>
            ) : (
              <Form.Item
                name="portalKey"
                label="Portal Key"
                rules={[{ required: true, message: "Portal key is required" }]}
              >
                <PortalKeySelectField
                  options={portalKeySelectOptions}
                  isLoading={portalKeysLoading}
                  isDisabled={!portalKeyEnabled}
                  placeholder={
                    portalKeyEnabled
                      ? "Select portal key"
                      : "Select purchaser first"
                  }
                  selectKey={`config-portal-key-${purchaserId ?? "none"}`}
                />
              </Form.Item>
            )}
          </Col>
        </Row>
        <Row>
          <Col span={6}>
            <Form.Item
              name="portalUserName"
              label="Portal User Name"
              rules={[
                { required: true, message: "Portal user name is required" },
              ]}
            >
              <Input placeholder="Portal user name" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="portalPassword"
              label="Portal Password"
              validateTrigger={["onChange", "onBlur"]}
              rules={
                mode === "create"
                  ? [{ required: true, message: "Password required" }]
                  : undefined
              }
            >
              <Input type="password" placeholder="Portal password" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Flex direction="column" gap={0.25} className="mt-1">
              <Paragraph className="m-0 custom-label">Status</Paragraph>
              <Form.Item name="isActive" valuePropName="isSelected" noStyle>
                <Switch
                  className="p-0 mt-1"
                  size="small"
                  aria-label="Portal credential active status"
                />
              </Form.Item>
            </Flex>
          </Col>
        </Row>
        <Row>
          <Col span={12} className="config-portal-credential-add-edit__metadata">
            <Flex direction="column" gap={0.5} className="w-100">
              <Paragraph className="m-0 custom-label">
                Metadata (JSON)
              </Paragraph>
              <Form.Item name="metadata" noStyle className="w-100">
                <JsonEditorField />
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
    </ErrorBoundary>
  );
};
